import { MicDeviceType } from "@/common/enums/enums";
import {
  NoiseReduction,
  TranscriptionNoRealtimeProviderName,
  TranscriptionProviderName,
} from "@/hooks/transcriber/types";
import type { MicDevice } from "@/hooks/useAudioInputDevices";
import { create } from "zustand";
import { useAppSettingsStore } from "./appSettings.store";

export interface RecordingSettingsState {
  inputLanguage: string;
  noiseReduction: NoiseReduction;
  transcriptionProvider: TranscriptionProviderName;
  transcriptionNoRealtimeProvider: TranscriptionNoRealtimeProviderName;
  saveInputLanguage: (value: string) => void;
  saveNoiseReduction: (value: NoiseReduction) => void;
  saveTranscriptionProvider: (value: TranscriptionProviderName) => void;
  saveTranscriptionNoRealtimeProvider: (
    value: TranscriptionNoRealtimeProviderName
  ) => void;
  isRealtimeEnabled: boolean;
  saveIsRealtimeEnabled: (value: boolean) => void;
  noiseSuppression: boolean;
  saveNoiseSuppression: (value: boolean) => void;
  diarize: boolean;
  saveDiarize: (value: boolean) => void;
  micDevice: MicDevice | null;
  saveMicDevice: (value: MicDevice) => void;
  getMicDeviceIdForReactMediaRecorder: () => string;
}

export const useRecordingSettingsStore = create<RecordingSettingsState>()(
  (set, get) => ({
    // TODO: get from user preference
    inputLanguage: useAppSettingsStore.getState().inputLanguage,
    noiseReduction: NoiseReduction.FAR_FIELD,
    transcriptionProvider: TranscriptionProviderName.OPENAI,
    isRealtimeEnabled: true,
    noiseSuppression: false,
    diarize: false,
    micDevice: null,
    syncMicrophone: true,
    transcriptionNoRealtimeProvider:
      TranscriptionNoRealtimeProviderName.DEEPGRAM_NOVA_3_MEDICAL,
    saveInputLanguage: (value: string): void => {
      set({
        inputLanguage: value,
      });
    },
    saveNoiseReduction: (value: NoiseReduction): void => {
      set({
        noiseReduction: value,
      });
    },
    saveTranscriptionProvider: (value: TranscriptionProviderName): void => {
      set({
        transcriptionProvider: value,
      });
    },
    saveIsRealtimeEnabled: (value: boolean): void => {
      set({
        isRealtimeEnabled: value,
        diarize: !value,
      });
    },
    saveNoiseSuppression: (value: boolean): void => {
      set({
        noiseSuppression: value,
      });
    },
    saveDiarize: (value: boolean): void => {
      set({
        diarize: value,
      });
    },
    saveMicDevice: (value: MicDevice): void => {
      set({
        micDevice: value,
      });
    },
    getMicDeviceIdForReactMediaRecorder: (): string => {
      if (get().micDevice?.type === MicDeviceType.DESKTOP) {
        return get().micDevice?.value || "default";
      } else {
        return "default";
      }
    },
    saveTranscriptionNoRealtimeProvider: (
      value: TranscriptionNoRealtimeProviderName
    ): void => {
      set({
        transcriptionNoRealtimeProvider: value,
      });
    },
  })
);
